import { useEffect, useRef, useState } from "react";
import { Plus, UploadSimple, Trash, Scan, Buildings } from "@phosphor-icons/react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { useCompany } from "../../context/CompanyContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { Input } from "../../components/ui/input";
import { Textarea } from "../../components/ui/textarea";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";

const today = () => new Date().toISOString().slice(0,10);
const emptyLog = { entity_id: "", log_date: today(), pages_scanned: 0, pages_digitized: 0, qc_passed_pages: 0, rework_pages: 0, hours_worked: 0, operator: "", batch_no: "", remarks: "" };

export default function MISLog() {
  const { active, activeId } = useCompany();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [entities, setEntities] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(emptyLog);
  const [month, setMonth] = useState(today().slice(0,7));
  const uploadRef = useRef(null);

  const load = async () => {
    if (!activeId) return;
    const [{ data: ls }, { data: es }] = await Promise.all([
      api.get("/mis/productivity", { params: { company_id: activeId, month } }),
      api.get("/mis/entities", { params: { company_id: activeId, only_enabled: true } }),
    ]);
    setLogs(ls);
    setEntities(es);
  };

  useEffect(() => { load(); }, [activeId, month]);

  const save = async () => {
    if (!form.entity_id) { toast.error("Select an entity"); return; }
    if (!form.log_date) { toast.error("Date required"); return; }
    const payload = {
      ...form,
      company_id: activeId,
      pages_scanned: parseInt(form.pages_scanned || 0, 10),
      pages_digitized: parseInt(form.pages_digitized || 0, 10),
      qc_passed_pages: parseInt(form.qc_passed_pages || 0, 10),
      rework_pages: parseInt(form.rework_pages || 0, 10),
      hours_worked: parseFloat(form.hours_worked || 0),
    };
    await api.post("/mis/productivity", payload);
    toast.success("Log added");
    setOpen(false); setForm(emptyLog); load();
  };

  const remove = async (id) => {
    if (!window.confirm("Delete log?")) return;
    await api.delete(`/mis/productivity/${id}`);
    toast.success("Deleted"); load();
  };

  const upload = async (e) => {
    const f = e.target.files?.[0]; if (!f) return;
    const fd = new FormData(); fd.append("file", f);
    try {
      const { data } = await api.post(`/mis/productivity/upload?company_id=${activeId}`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      const skipDetail = data.skipped ? ` · ${data.skipped} skipped` : "";
      const unknown = data.unknown_entities?.length ? ` (unknown: ${data.unknown_entities.slice(0,3).join(", ")}…)` : "";
      toast.success(`Imported ${data.inserted} logs${skipDetail}${unknown}`);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Upload failed");
    } finally { e.target.value = ""; }
  };

  if (!activeId) {
    return (
      <Layout title="Daily Productivity Log">
        <div className="border border-black/10 p-16 text-center">
          <Buildings size={36} className="mx-auto text-[#D90429]"/>
          <h3 className="font-display text-2xl font-bold tracking-tight mt-4">Pick a company first</h3>
          <button onClick={() => navigate("/finance/companies")} className="mt-5 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Go to Companies</button>
        </div>
      </Layout>
    );
  }

  const entityById = Object.fromEntries(entities.map((e) => [e.id, e]));

  return (
    <Layout
      title="Daily Productivity Log"
      subtitle={`${logs.length} entries · ${active?.name || ""}`}
      actions={
        <>
          <Input type="month" value={month} onChange={(e)=>setMonth(e.target.value)} className="w-44 rounded-none" data-testid="log-month-picker"/>
          <input ref={uploadRef} type="file" accept=".csv,.xlsx,.xls" onChange={upload} className="hidden" data-testid="log-upload-input"/>
          <button onClick={()=>uploadRef.current?.click()} data-testid="log-upload-button" className="inline-flex items-center gap-2 px-4 py-2.5 border border-black/15 text-sm font-semibold hover:bg-[#0A0A0A] hover:text-white">
            <UploadSimple size={16} weight="bold"/> Import CSV
          </button>
          <button onClick={()=>{setForm(emptyLog); setOpen(true);}} data-testid="add-log-button" className="inline-flex items-center gap-2 px-4 py-2.5 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C] active:scale-95">
            <Plus size={16} weight="bold"/> Add Log
          </button>
        </>
      }
    >
      {entities.length === 0 && (
        <div className="border border-[#F4A261]/40 bg-[#F4A261]/5 p-4 mb-5 text-sm">
          <strong>No MIS-enabled clients yet.</strong> Go to <button onClick={()=>navigate("/mis/entities")} className="text-[#D90429] font-semibold underline">Billing Entities</button> and turn on MIS billing for the clients you scan for.
        </div>
      )}

      {logs.length === 0 ? (
        <div className="border border-black/10 p-16 text-center text-[#717680]"><Scan size={36} className="mx-auto text-[#D90429]"/><p className="mt-4">No logs for {month}. Add daily entries or import a CSV.</p>
          <p className="mt-3 text-xs font-mono-shelv">CSV columns: date, entity_name, pages_scanned, pages_digitized, qc_passed, rework_pages, hours_worked, operator, batch_no, remarks</p>
        </div>
      ) : (
        <div className="border border-black/10 bg-white overflow-x-auto">
          <table className="w-full text-sm min-w-[640px]" data-testid="productivity-table">
            <thead className="bg-[#F5F6F8] text-left"><tr>{["Date","Entity","Scanned","Digitized","QC Passed","Rework","Hrs","Operator","Batch","Remarks",""].map(h=>(<th key={h} className="px-3 py-3 font-mono-shelv text-xs uppercase tracking-widest text-[#717680]">{h}</th>))}</tr></thead>
            <tbody>
              {logs.map((l)=>(
                <tr key={l.id} className="border-t border-black/5 hover:bg-[#FAFBFC]" data-testid={`log-row-${l.id}`}>
                  <td className="px-3 py-2 font-mono-shelv text-xs">{l.log_date}</td>
                  <td className="px-3 py-2 font-semibold">{entityById[l.entity_id]?.name || "—"}</td>
                  <td className="px-3 py-2 font-mono-shelv">{l.pages_scanned.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 font-mono-shelv">{l.pages_digitized.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 font-mono-shelv text-[#2A9D8F]">{l.qc_passed_pages.toLocaleString("en-IN")}</td>
                  <td className="px-3 py-2 font-mono-shelv text-[#D90429]">{l.rework_pages || 0}</td>
                  <td className="px-3 py-2 font-mono-shelv">{l.hours_worked || 0}</td>
                  <td className="px-3 py-2 text-[#4A4D57]">{l.operator || "—"}</td>
                  <td className="px-3 py-2 text-[#4A4D57] font-mono-shelv text-xs">{l.batch_no || "—"}</td>
                  <td className="px-3 py-2 text-[#4A4D57] truncate max-w-[10rem]">{l.remarks || ""}</td>
                  <td className="px-3 py-2 text-right"><button onClick={()=>remove(l.id)} className="p-1.5 hover:bg-[#D90429]/10 text-[#D90429]" data-testid={`delete-log-${l.id}`}><Trash size={13}/></button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="rounded-none max-w-2xl">
          <DialogHeader><DialogTitle className="font-display tracking-tight">Log daily productivity</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label>Entity *</Label>
              <Select value={form.entity_id} onValueChange={(v)=>setForm({...form, entity_id:v})}>
                <SelectTrigger className="rounded-none" data-testid="log-entity-select"><SelectValue placeholder="Pick an entity…"/></SelectTrigger>
                <SelectContent>{entities.map((e)=><SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Date *</Label><Input type="date" value={form.log_date} onChange={(e)=>setForm({...form, log_date:e.target.value})} className="rounded-none" data-testid="log-date-input"/></div>
            <div><Label>Pages Scanned</Label><Input type="number" value={form.pages_scanned} onChange={(e)=>setForm({...form, pages_scanned:e.target.value})} className="rounded-none" data-testid="pages-scanned-input"/></div>
            <div><Label>Pages Digitized</Label><Input type="number" value={form.pages_digitized} onChange={(e)=>setForm({...form, pages_digitized:e.target.value})} className="rounded-none"/></div>
            <div><Label>QC Passed Pages</Label><Input type="number" value={form.qc_passed_pages} onChange={(e)=>setForm({...form, qc_passed_pages:e.target.value})} className="rounded-none" data-testid="qc-passed-input"/></div>
            <div><Label>Rework Pages</Label><Input type="number" value={form.rework_pages} onChange={(e)=>setForm({...form, rework_pages:e.target.value})} className="rounded-none"/></div>
            <div><Label>Hours Worked</Label><Input type="number" step="0.5" value={form.hours_worked} onChange={(e)=>setForm({...form, hours_worked:e.target.value})} className="rounded-none"/></div>
            <div><Label>Operator</Label><Input value={form.operator} onChange={(e)=>setForm({...form, operator:e.target.value})} className="rounded-none"/></div>
            <div><Label>Batch No.</Label><Input value={form.batch_no} onChange={(e)=>setForm({...form, batch_no:e.target.value})} className="rounded-none"/></div>
            <div className="col-span-2"><Label>Remarks</Label><Textarea rows={2} value={form.remarks} onChange={(e)=>setForm({...form, remarks:e.target.value})} className="rounded-none"/></div>
          </div>
          <DialogFooter>
            <button onClick={()=>setOpen(false)} className="px-4 py-2 border border-black/15 text-sm">Cancel</button>
            <button onClick={save} data-testid="save-log-button" className="px-4 py-2 bg-[#D90429] text-white text-sm font-semibold hover:bg-[#EF233C]">Save</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
